import { createContext, useCallback, useContext, useEffect, useRef } from 'react';

export type LocationLike = { pathname: string; search: string };
export type Draft = {
    active: boolean;
    dirty: () => boolean;
    save: () => Promise<boolean | void>;
    discard: () => void;
    leaves: (current: LocationLike, next: LocationLike) => boolean;
};
export type PendingEdit = { dirty: () => boolean; flush: () => void; cancel: () => void };
export type Ref<T> = { current: T };
export type Controls = {
    register: (draft: Ref<Draft>) => () => void;
    registerPending: (edit: Ref<PendingEdit>) => () => void;
    requestAction: (action: () => void) => void;
    withoutWarning: (action: () => void) => void;
    afterSave: (action: () => void) => void;
    flushPending?: () => void;
};
export const Context = createContext<Controls | null>(null);
const immediate = (action: () => void) => action();
const noop = () => {};

export function useUnsavedActions() {
    const controls = useContext(Context);
    return { requestAction: controls?.requestAction ?? immediate, withoutWarning: controls?.withoutWarning ?? immediate,
        afterSave: controls?.afterSave ?? immediate, flushPending: controls?.flushPending ?? noop };
}

export function useUnsavedDraft({ active, value, baselineValue = value, parameter, save, discard }: {
    active: boolean; value: unknown; parameter: string;
    baselineValue?: unknown;
    save: () => Promise<boolean | void>; discard: () => void;
}) {
    const controls = useContext(Context);
    const baseline = useRef('');
    const wasActive = useRef(false);
    const currentValue = useRef(value);
    currentValue.current = value;
    if (!active || !wasActive.current) baseline.current = JSON.stringify(baselineValue);
    wasActive.current = active;
    const draft = useRef<Draft>(null!);
    draft.current = { active, dirty: () => JSON.stringify(currentValue.current) !== baseline.current,
        save, discard, leaves: (current, next) => current.pathname !== next.pathname
            || JSON.stringify(new URLSearchParams(current.search).getAll(parameter)) !== JSON.stringify(new URLSearchParams(next.search).getAll(parameter)) };
    const register = controls?.register;
    useEffect(() => register?.(draft), [register]);
    const markSaved = useCallback((saved: unknown) => {
        baseline.current = JSON.stringify(saved); currentValue.current = saved;
    }, []);
    return { markSaved };
}

export function usePendingEdit(edit: PendingEdit) {
    const controls = useContext(Context);
    const ref = useRef(edit);
    ref.current = edit;
    const register = controls?.registerPending;
    useEffect(() => register?.(ref), [register]);
}
